import axios from "axios";
import { useAuthStore } from "@/store/auth.store";

const BASE_URL = process.env["NEXT_PUBLIC_API_URL"] ?? "http://localhost:4000";

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // sends httpOnly refresh token cookie automatically
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (refreshToken) {
    config.headers["x-refresh-token"] = refreshToken;
  }
  return config;
});

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

function drainQueue(token: string) {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshing = true;
    try {
      const rt = useAuthStore.getState().refreshToken;
      const { data } = await axios.post(
        `${BASE_URL}/api/v1/auth/refresh`,
        {},
        { 
          withCredentials: true,
          headers: { "x-refresh-token": rt ?? "" }
        }
      );
      const newToken: string = data.data.accessToken;
      const newRefreshToken: string = data.data.refreshToken;
      useAuthStore.getState().setAccessToken(newToken, newRefreshToken);
      drainQueue(newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch {
      try {
        const rt = useAuthStore.getState().refreshToken;
        await axios.post(
          `${BASE_URL}/api/v1/auth/logout`,
          {},
          { withCredentials: true, headers: { "x-refresh-token": rt ?? "" } }
        );
      } catch {
        // ignore — cookie will be cleared best-effort
      }
      useAuthStore.getState().logout();
      if (typeof window !== "undefined") window.location.href = "/login";
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

export async function extendSession(): Promise<string | null> {
  try {
    const rt = useAuthStore.getState().refreshToken;
    const { data } = await axios.post(
      `${BASE_URL}/api/v1/auth/refresh`,
      {},
      { 
        withCredentials: true,
        headers: { "x-refresh-token": rt ?? "" }
      }
    );
    const newToken: string = data.data.accessToken;
    const newRefreshToken: string = data.data.refreshToken;
    useAuthStore.getState().setAccessToken(newToken, newRefreshToken);
    return newToken;
  } catch {
    return null;
  }
}
